import React, { useEffect, useMemo, useRef, useState } from "react";
import Papa from "papaparse";
import { api, formatError } from "../lib/api";
import { AppLayout } from "../components/tabon/AppLayout";
import { formatCurrency, formatDate, segmentColor, statusColor } from "../lib/format";
import { Download, Pencil, Search, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

export default function Customers() {
  const { user, hasFeature } = useAuth();
  const hasLoyalty = hasFeature("loyalty_points");
  const hasBottleLocker = hasFeature("bottle_locker");

  const [rows, setRows] = useState([]);
  const [showImport, setShowImport] = useState(false);
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("last_visit_at");
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [drawerTab, setDrawerTab] = useState("profile");
  const [pointsHistory, setPointsHistory] = useState([]);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [tabDeductions, setTabDeductions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [visitLogs, setVisitLogs] = useState([]); // ordering/POS visits (visit_logs)
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [listDateFrom, setListDateFrom] = useState("");
  const [listDateTo, setListDateTo] = useState("");
  const [bottleDateFrom, setBottleDateFrom] = useState("");
  const [bottleDateTo, setBottleDateTo] = useState("");

  useEffect(() => {
    api.get("/customers").then(({ data }) => setRows(data));
  }, []);

  const filtered = useMemo(() => {
    let arr = rows;
    if (q.trim()) {
      const qq = q.toLowerCase();
      arr = arr.filter(
        (c) => c.name?.toLowerCase().includes(qq) || c.phone?.includes(q)
      );
    }
    if (filter !== "All") arr = arr.filter((c) => c.segment === filter);
    if (listDateFrom) arr = arr.filter((c) => !c.last_visit_at || c.last_visit_at.slice(0, 10) >= listDateFrom);
    if (listDateTo) arr = arr.filter((c) => !c.last_visit_at || c.last_visit_at.slice(0, 10) <= listDateTo);
    return [...arr].sort((a, b) => {
      const av = a[sortKey] || "";
      const bv = b[sortKey] || "";
      if (sortKey === "total_visits" || sortKey === "total_spend" || sortKey === "active_tabs_count") return (bv || 0) - (av || 0);
      return String(bv).localeCompare(String(av));
    });
  }, [rows, q, filter, sortKey, listDateFrom, listDateTo]);

  const openDetail = async (c) => {
    setActive(c);
    setDrawerTab("profile");
    setDetail(null);
    setPointsHistory([]);
    setTabDeductions([]);
    setTransactions([]);
    setEditingName(false);
    setNameInput(c.name);
    setDateFrom("");
    setDateTo("");
    setBottleDateFrom("");
    setBottleDateTo("");
    const { data } = await api.get(`/customers/${c.id}`);
    setDetail(data);
    if (hasLoyalty) {
      const { data: pts } = await api.get(`/customers/${c.id}/points`);
      setPointsHistory(pts);
    }
    const { data: txns } = await api.get(`/staff/billing/transactions?customer_id=${c.id}`);
    setTransactions(txns);
    try {
      const { data: vlogs } = await api.get(`/visits?customer_id=${c.id}`);
      setVisitLogs(vlogs || []);
    } catch { setVisitLogs([]); }
    if (hasBottleLocker) {
      const { data: deds } = await api.get(`/customers/${c.id}/tab-deductions`);
      setTabDeductions(deds);
    }
  };

  const saveName = async () => {
    if (!nameInput.trim() || nameInput.trim() === active.name) { setEditingName(false); return; }
    setSavingName(true);
    try {
      await api.patch(`/customers/${active.id}`, { name: nameInput.trim() });
      const updated = nameInput.trim();
      setActive(prev => ({ ...prev, name: updated }));
      setRows(prev => prev.map(r => r.id === active.id ? { ...r, name: updated } : r));
      setEditingName(false);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSavingName(false);
    }
  };

  const filteredPts = useMemo(() => {
    return pointsHistory.filter(t => {
      const d = t.created_at?.slice(0, 10) || "";
      if (dateFrom && d < dateFrom) return false;
      if (dateTo && d > dateTo) return false;
      return true;
    });
  }, [pointsHistory, dateFrom, dateTo]);

  const drawerTabs = [
    { key: "profile", label: "Profile" },
    { key: "transactions", label: "Visits" },
    ...(hasLoyalty ? [{ key: "points", label: "Points History" }] : []),
    ...(hasBottleLocker ? [{ key: "bottles", label: "Bottle Tabs" }] : []),
  ];

  return (
    <AppLayout>
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        <div className="mb-5">
          <h1 className="text-3xl md:text-4xl font-black tracking-tight">Customers</h1>
          <p className="text-sm text-[#A0A0A0]">All customers across your venue</p>
        </div>

        <div className="flex flex-wrap gap-3 mb-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0A0A0]" />
            <input
              data-testid="customer-table-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search customers…"
              className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg pl-9 pr-4 py-2.5 min-h-[44px] text-white focus:border-[#9D00FF] focus:ring-1 focus:ring-[#9D00FF]/40"
            />
          </div>
          <select
            data-testid="segment-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2.5 min-h-[44px] text-white focus:border-[#9D00FF]"
          >
            {["All", "New", "Regular", "At Risk", "Lost"].map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
          <select
            data-testid="sort-key"
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            className="bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2.5 min-h-[44px] text-white focus:border-[#9D00FF]"
          >
            <option value="last_visit_at">Last Visit</option>
            <option value="total_visits">Total Visits</option>
            <option value="total_spend">Total Spend</option>
            <option value="created_at">Date Added</option>
            {hasBottleLocker && (
              <option value="active_tabs_count">Active Tabs</option>
            )}
          </select>
          <div className="flex items-center gap-2 flex-wrap text-xs text-[#A0A0A0]">
            <span>Last visit from</span>
            <input
              type="date"
              value={listDateFrom}
              onChange={(e) => setListDateFrom(e.target.value)}
              className="bg-[#1A1A1A] border border-white/10 rounded-lg px-2 py-2 text-white text-xs focus:border-[#9D00FF] min-h-[40px]"
            />
            <span>to</span>
            <input
              type="date"
              value={listDateTo}
              onChange={(e) => setListDateTo(e.target.value)}
              className="bg-[#1A1A1A] border border-white/10 rounded-lg px-2 py-2 text-white text-xs focus:border-[#9D00FF] min-h-[40px]"
            />
            {(listDateFrom || listDateTo) && (
              <button
                type="button"
                onClick={() => { setListDateFrom(""); setListDateTo(""); }}
                className="text-[#A0A0A0] hover:text-white text-xs"
              >
                Clear
              </button>
            )}
          </div>
          <button
            data-testid="import-customers-btn"
            onClick={() => setShowImport(true)}
            className="bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2.5 min-h-[44px] text-white hover:border-[#9D00FF] flex items-center gap-2 text-sm font-semibold"
          >
            <Upload size={15} /> Import
          </button>
        </div>

        <div className="bg-[#1A1A1A] border border-white/10 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[#A0A0A0] text-xs uppercase tracking-wider bg-[#0A0A0A]">
                <tr>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Phone</th>
                  <th className="text-left px-4 py-3">Last Visit</th>
                  <th className="text-left px-4 py-3">Visits</th>
                  <th className="text-left px-4 py-3">Avg Spend</th>
                  {hasBottleLocker && (
                    <th className="text-left px-4 py-3">Active Tabs</th>
                  )}
                  {hasLoyalty && (
                    <th className="text-left px-4 py-3">Points</th>
                  )}
                  <th className="text-left px-4 py-3">Segment</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-[#A0A0A0]">
                      No customers yet.
                    </td>
                  </tr>
                )}
                {filtered.map((c) => (
                  <tr
                    key={c.id}
                    data-testid={`customer-row-${c.id}`}
                    onClick={() => openDetail(c)}
                    className="border-t border-white/5 hover:bg-white/5 cursor-pointer"
                  >
                    <td className="px-4 py-3 font-semibold text-white">{c.name}</td>
                    <td className="px-4 py-3 text-[#A0A0A0]">{c.phone}</td>
                    <td className="px-4 py-3">{formatDate(c.last_visit_at)}</td>
                    <td className="px-4 py-3">{c.total_visits}</td>
                    <td className="px-4 py-3">
                      {c.total_visits > 0
                        ? formatCurrency(c.total_spend / c.total_visits)
                        : "—"}
                    </td>
                    {hasBottleLocker && (
                      <td className="px-4 py-3">
                        {c.active_tabs_count > 0 ? (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-md border bg-[#9D00FF]/15 text-[#D78CFF] border-[#9D00FF]/40">
                            {c.active_tabs_count} active
                          </span>
                        ) : (
                          <span className="text-[#A0A0A0]">—</span>
                        )}
                      </td>
                    )}
                    {hasLoyalty && (
                      <td className="px-4 py-3 font-bold text-[#9D00FF]" data-testid={`customer-points-${c.id}`}>
                        🏆 {c.points_balance || 0}
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-md border uppercase tracking-wider font-bold ${segmentColor(c.segment)}`}>
                        {c.segment}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {active && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-end"
          onClick={() => setActive(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-[#1A1A1A] border-l border-white/10 h-full w-full sm:w-[520px] p-6 overflow-y-auto tb-fade-up"
          >
            {/* Header with name edit */}
            <div className="flex items-start justify-between mb-1">
              {editingName ? (
                <div className="flex items-center gap-2 flex-1">
                  <input
                    autoFocus
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditingName(false); }}
                    className="bg-[#0A0A0A] border border-[#9D00FF] rounded-lg px-3 py-1.5 text-white text-lg font-bold flex-1"
                  />
                  <button
                    onClick={saveName}
                    disabled={savingName}
                    className="px-3 py-1.5 rounded-lg bg-[#9D00FF] text-white text-xs font-semibold disabled:opacity-50"
                  >
                    {savingName ? "…" : "Save"}
                  </button>
                  <button onClick={() => setEditingName(false)} className="text-[#A0A0A0] hover:text-white text-xs">
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 flex-1">
                  <h2 className="text-xl font-bold">{active.name}</h2>
                  <button
                    onClick={() => { setEditingName(true); setNameInput(active.name); }}
                    className="text-[#A0A0A0] hover:text-white"
                    title="Edit name"
                  >
                    <Pencil size={14} />
                  </button>
                </div>
              )}
              <button onClick={() => setActive(null)} className="p-2 text-[#A0A0A0] hover:text-white ml-2 shrink-0">
                <X size={18} />
              </button>
            </div>
            <div className="text-sm text-[#A0A0A0] mb-4">{active.phone}</div>

            {/* Tab pills */}
            <div className="flex flex-wrap items-center gap-2 mb-5">
              {drawerTabs.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setDrawerTab(t.key)}
                  data-testid={`drawer-tab-${t.key}`}
                  className={`text-xs font-bold uppercase tracking-wider rounded-full px-3 py-1 border ${
                    drawerTab === t.key ? "bg-[#9D00FF] border-[#9D00FF] text-white" : "border-white/10 text-[#A0A0A0]"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Profile tab */}
            {drawerTab === "profile" && (
              <>
                <div className={`grid gap-3 ${hasLoyalty ? "grid-cols-3" : "grid-cols-2"}`}>
                  <Stat label="Visits" value={active.total_visits} />
                  <Stat label="Total Spend" value={formatCurrency(active.total_spend)} />
                  {hasLoyalty && (
                    <Stat label="🏆 Points" value={active.points_balance || 0} />
                  )}
                </div>
                {hasBottleLocker && (
                  <div className="mt-6">
                    <h3 className="font-semibold mb-2">Active Tabs</h3>
                    {(detail?.active_tabs || []).length === 0 ? (
                      <p className="text-sm text-[#A0A0A0]">None.</p>
                    ) : (
                      detail.active_tabs.map((t) => (
                        <div key={t.id} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3 mb-2">
                          <div className="font-semibold">{t.brand}</div>
                          <div className="text-xs text-[#A0A0A0]">
                            {t.remaining_ml}ml · expires {formatDate(t.expiry_at)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
                {/* Joined via */}
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-xs text-[#A0A0A0]">Joined via</span>
                    <span className="text-xs font-medium text-white">
                      {(() => {
                        const map = {
                          billing_counter: "🧾 Billing counter",
                          menu_scan: "📱 Menu QR",
                          delivery: "🛵 Delivery QR",
                          menu_bottle_check: "🍾 Bottle check",
                          bulk_import: "📥 Bulk import",
                          split_claim: "🔗 Points split",
                          ordering: "📲 QR Ordering",
                          pos_petpooja: "🖥️ POS · Petpooja",
                        };
                        return map[active.source] || "—";
                      })()}
                    </span>
                  </div>
                </div>
              </>
            )}

            {/* Visits tab — merges Billing Counter settlements (billing_transactions)
                with ordering/POS visits (visit_logs). Only visit_logs with an
                order_id are included: Billing Counter also writes a visit_log via
                ensure_visit_today, but those have no order_id and are already
                represented by their billing_transactions row — so keying on
                order_id prevents duplicate rows. */}
            {drawerTab === "transactions" && (() => {
              const merged = [
                ...transactions.map((t) => ({
                  key: `bill_${t.id}`, date: t.settled_at, isOrder: false,
                  billNo: t.bill_number || "—", amount: t.bill_amount,
                  netPayable: t.net_payable, points: t.points_earned,
                  settledBy: t.staff_name || "", sourceLabel: "Billing counter",
                })),
                ...visitLogs.filter((v) => v.order_id).map((v) => ({
                  key: `visit_${v.id}`, date: v.visit_at || v.created_at, isOrder: true,
                  billNo: v.order_number || v.order_id, amount: v.bill_amount,
                  netPayable: null, points: v.points_earned,
                  settledBy: "", sourceLabel: "Order",
                })),
              ].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
              return (
                <div className="mt-1">
                  {merged.length === 0 ? (
                    <p className="text-sm text-[#A0A0A0]">No visits recorded yet.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="text-[#A0A0A0] text-[10px] uppercase tracking-wider">
                          <tr>
                            <th className="text-left py-2 pr-3">Date</th>
                            <th className="text-left py-2 pr-3">Source</th>
                            <th className="text-left py-2 pr-3">Bill / Order No.</th>
                            <th className="text-left py-2 pr-3">Bill Amt</th>
                            <th className="text-left py-2 pr-3">Net Payable</th>
                            {hasLoyalty && <th className="text-left py-2 pr-3">Pts Earned</th>}
                            <th className="text-left py-2">Settled by</th>
                          </tr>
                        </thead>
                        <tbody>
                          {merged.map((t) => (
                            <tr key={t.key} className="border-t border-white/5">
                              <td className="py-2.5 pr-3 text-[#A0A0A0]">{formatDate(t.date)}</td>
                              <td className="py-2.5 pr-3">
                                <span className={`text-[9px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 border ${t.isOrder ? "bg-sky-500/15 text-sky-300 border-sky-500/40" : "bg-white/5 text-[#A0A0A0] border-white/20"}`}>
                                  {t.sourceLabel}
                                </span>
                              </td>
                              <td className="py-2.5 pr-3 font-mono text-xs text-[#A0A0A0]">{t.isOrder ? `Order #${t.billNo}` : t.billNo}</td>
                              <td className="py-2.5 pr-3 font-semibold">{t.amount ? formatCurrency(t.amount) : "—"}</td>
                              <td className="py-2.5 pr-3">{t.netPayable != null ? formatCurrency(t.netPayable) : "—"}</td>
                              {hasLoyalty && (
                                <td className="py-2.5 pr-3 font-bold text-emerald-300">
                                  {t.points ? `+${t.points}` : "—"}
                                </td>
                              )}
                              <td className="py-2.5 text-[#A0A0A0] text-xs">{t.settledBy || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Points history tab */}
            {drawerTab === "points" && hasLoyalty && (
              <div className="mt-1">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:border-[#9D00FF]"
                  />
                  <span className="text-xs text-[#A0A0A0]">to</span>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:border-[#9D00FF]"
                  />
                  {(dateFrom || dateTo) && (
                    <button
                      type="button"
                      onClick={() => { setDateFrom(""); setDateTo(""); }}
                      className="text-xs text-[#A0A0A0] hover:text-white border border-white/10 rounded-lg px-2 py-1.5"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <div className="overflow-x-auto" data-testid="points-history-table">
                  <table className="w-full text-sm">
                    <thead className="text-[#A0A0A0] text-[10px] uppercase tracking-wider">
                      <tr>
                        <th className="text-left py-2 pr-2">Date</th>
                        <th className="text-left py-2 pr-2">Type</th>
                        <th className="text-left py-2 pr-2">Points</th>
                        <th className="text-left py-2 pr-2">Bill No.</th>
                        <th className="text-left py-2 pr-2">Reference</th>
                        <th className="text-left py-2">By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPts.length === 0 && (
                        <tr><td colSpan={6} className="text-center py-4 text-[#A0A0A0]">No points transactions.</td></tr>
                      )}
                      {filteredPts.map((t) => (
                        <tr key={t.id} className="border-t border-white/5">
                          <td className="py-2 pr-2 text-[#A0A0A0]">{formatDate(t.created_at)}</td>
                          <td className="py-2 pr-2">
                            <div className="flex items-center gap-1 flex-wrap">
                              {(() => {
                                const isManual = t.reason === "manual" || t.reason === "signup_bonus" || t.reason === "cover";
                                const isRedeem = (t.points ?? t.delta) < 0;
                                const cls = isManual
                                  ? "bg-[#F59E0B]/15 text-[#F59E0B] border-[#F59E0B]/40"
                                  : isRedeem
                                  ? "bg-[#9D00FF]/15 text-[#D78CFF] border-[#9D00FF]/40"
                                  : "bg-emerald-500/15 text-emerald-300 border-emerald-500/40";
                                const label = isManual ? "manual" : isRedeem ? "redeem" : "earn";
                                return (
                                  <span className={`text-[10px] font-bold uppercase rounded-full px-2 py-0.5 border ${cls}`}>
                                    {label}
                                  </span>
                                );
                              })()}
                              {t.reason === "redeem" && t.otp_bypassed && (
                                <span title="Customer OTP verification was skipped"
                                  className="text-[9px] font-bold uppercase rounded-full px-1.5 py-0.5 border bg-[#F59E0B]/15 text-[#F59E0B] border-[#F59E0B]/40">
                                  OTP bypassed
                                </span>
                              )}
                              {t.reason === "redeem" && !t.otp_bypassed && t.otp_verified && (
                                <span title="Verified with customer OTP"
                                  className="text-[9px] font-bold uppercase rounded-full px-1.5 py-0.5 border bg-emerald-500/10 text-emerald-300/80 border-emerald-500/30">
                                  verified
                                </span>
                              )}
                            </div>
                          </td>
                          <td className={`py-2 pr-2 font-bold ${(t.points ?? t.delta) > 0 ? "text-emerald-300" : "text-[#D78CFF]"}`}>
                            {(t.points ?? t.delta) > 0 ? `+${t.points ?? t.delta}` : (t.points ?? t.delta)}
                          </td>
                          <td className="py-2 pr-2 text-[#A0A0A0] font-mono text-xs">{t.bill_number || "—"}</td>
                          <td className="py-2 pr-2 text-[#A0A0A0]">{t.reference || t.reason || "—"}</td>
                          <td className="py-2 text-[#A0A0A0] text-xs">{t.adjusted_by || t.performed_by || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Bottle tabs tab */}
            {drawerTab === "bottles" && hasBottleLocker && (
              <div className="mt-1">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <span className="text-xs text-[#A0A0A0]">Opened from</span>
                  <input
                    type="date"
                    value={bottleDateFrom}
                    onChange={(e) => setBottleDateFrom(e.target.value)}
                    className="bg-[#0A0A0A] border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs focus:border-[#9D00FF]"
                  />
                  <span className="text-xs text-[#A0A0A0]">to</span>
                  <input
                    type="date"
                    value={bottleDateTo}
                    onChange={(e) => setBottleDateTo(e.target.value)}
                    className="bg-[#0A0A0A] border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs focus:border-[#9D00FF]"
                  />
                  {(bottleDateFrom || bottleDateTo) && (
                    <button
                      type="button"
                      onClick={() => { setBottleDateFrom(""); setBottleDateTo(""); }}
                      className="text-xs text-[#A0A0A0] hover:text-white"
                    >
                      Clear
                    </button>
                  )}
                </div>
                {(() => {
                  const filteredTabs = (detail?.all_tabs || []).filter((t) => {
                    const d = (t.created_at || "").slice(0, 10);
                    if (bottleDateFrom && d < bottleDateFrom) return false;
                    if (bottleDateTo && d > bottleDateTo) return false;
                    return true;
                  });
                  if (filteredTabs.length === 0) {
                    return <p className="text-sm text-[#A0A0A0]">No bottle tabs yet.</p>;
                  }
                  return (
                    <div className="flex flex-col gap-5">
                      {filteredTabs.map((t) => {
                        const serves = tabDeductions.filter(d => d.tab_id === t.id);
                        const pct = t.bottle_size_ml > 0
                          ? Math.min(100, Math.round((t.remaining_ml / t.bottle_size_ml) * 100))
                          : 0;
                        return (
                          <div key={t.id} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-semibold text-white">{t.brand}</span>
                              <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 border ${statusColor(t.status)}`}>
                                {t.status}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 mb-1">
                              <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                                <div className="h-1.5 rounded-full" style={{ width: `${pct}%`, background: t.status === "Expiring Soon" ? "#F59E0B" : "#9D00FF" }} />
                              </div>
                              <span className="text-xs text-[#A0A0A0]">{t.remaining_ml}ml / {t.bottle_size_ml}ml</span>
                            </div>
                            <div className="text-xs text-[#A0A0A0] mb-1">
                              Opened {formatDate(t.created_at)}
                              {t.bill_number && <span className="ml-2 font-mono">#{t.bill_number}</span>}
                            </div>
                            <div className="text-xs text-[#A0A0A0] mb-3">
                              Expires {formatDate(t.expiry_at)}
                              {t.staff_name && <span className="ml-2">· Logged by <span className="text-white">{t.staff_name}</span></span>}
                            </div>
                            {serves.length > 0 && (
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                  <thead className="text-[#A0A0A0] text-[10px] uppercase tracking-wider">
                                    <tr>
                                      <th className="text-left py-1.5 pr-3">Date</th>
                                      <th className="text-left py-1.5 pr-3">Pegs</th>
                                      <th className="text-left py-1.5 pr-3">ml</th>
                                      <th className="text-left py-1.5 pr-3">Bill No.</th>
                                      <th className="text-left py-1.5 pr-3">Verified</th>
                                      <th className="text-left py-1.5">Logged by</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {serves.map((d) => (
                                      <tr key={d.id} className="border-t border-white/5">
                                        <td className="py-1.5 pr-3 text-[#A0A0A0]">{formatDate(d.created_at)}</td>
                                        <td className="py-1.5 pr-3">{d.num_pegs} × {d.peg_size_ml}ml</td>
                                        <td className="py-1.5 pr-3 font-semibold text-[#9D00FF]">{d.total_ml}ml</td>
                                        <td className="py-1.5 pr-3 font-mono text-[#A0A0A0]">{d.bill_number || "—"}</td>
                                        <td className="py-1.5 pr-3">
                                          {d.otp_verified ? (
                                            <span className="text-emerald-300 font-bold">OTP</span>
                                          ) : d.otp_bypassed ? (
                                            <span className="text-amber-400 font-bold">Override</span>
                                          ) : (
                                            <span className="text-[#A0A0A0]">—</span>
                                          )}
                                        </td>
                                        <td className="py-1.5 text-[#A0A0A0] text-xs">{d.staff_name || "—"}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                            {serves.length === 0 && (
                              <p className="text-xs text-[#A0A0A0]">No serves recorded for this tab.</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      )}
      {showImport && (
        <ImportModal
          venue={user?.venue}
          onClose={() => setShowImport(false)}
          onDone={() => {
            setShowImport(false);
            api.get("/customers").then(({ data }) => setRows(data));
          }}
        />
      )}
    </AppLayout>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0]">{label}</div>
      <div className="font-bold text-white mt-0.5">{value ?? "—"}</div>
    </div>
  );
}

const CSV_TEMPLATE = "name,phone,dob,anniversary\nJane Doe,9876543210,1990-05-15,2015-11-20\n";

function ImportModal({ venue, onClose, onDone }) {
  const fileRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const sendWelcome = (venue?.signup_bonus_points > 0) && (venue?.points_expiry_months > 0);
  const showBonusNoExpiryWarning =
    (venue?.signup_bonus_points > 0) && !(venue?.points_expiry_months > 0);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: ({ data, errors: parseErrors }) => {
        if (parseErrors.length) {
          toast.error("CSV parse error: " + parseErrors[0].message);
          return;
        }
        const cleaned = data.map((r) => ({
          name: (r.name || r.Name || "").trim(),
          phone: (r.phone || r.Phone || r.mobile || r.Mobile || "").trim(),
          dob: (r.dob || r.DOB || r["Date of Birth"] || "").trim() || null,
          anniversary: (r.anniversary || r.Anniversary || "").trim() || null,
          whatsapp_opt_in: true,
        })).filter((r) => r.name && r.phone);
        setRows(cleaned);
        setErrors([]);
      },
    });
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "customers_template.csv";
    a.click();
  };

  const doImport = async () => {
    if (!rows.length) { toast.error("No valid rows to import"); return; }
    setImporting(true);
    try {
      const { data } = await api.post("/customers/bulk", {
        customers: rows,
        send_welcome: sendWelcome,
      });
      toast.success(`Imported ${data.created} customers${data.skipped ? `, ${data.skipped} skipped (duplicates)` : ""}`);
      if (data.errors?.length) setErrors(data.errors);
      else onDone();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#1A1A1A] border border-white/10 rounded-xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">Import Customers</h2>
          <button onClick={onClose} className="text-[#A0A0A0] hover:text-white"><X size={18} /></button>
        </div>
        <p className="text-xs text-[#A0A0A0] mb-4">Upload a CSV with columns: <span className="font-mono text-white">name, phone, dob, anniversary</span> (dob and anniversary optional, format YYYY-MM-DD).</p>
        {showBonusNoExpiryWarning && (
          <div className="mb-4 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
            <p className="text-xs font-semibold text-amber-300 mb-1">
              ⚠ WhatsApp welcome messages will not be sent
            </p>
            <p className="text-xs text-amber-200/80">
              A signup bonus of {venue.signup_bonus_points} pts will be awarded to each imported customer,
              but WhatsApp welcome messages require Points Expiry to be configured.
              Go to <span className="font-semibold text-amber-300">Settings → Loyalty → Points Expiry</span> to enable them.
            </p>
          </div>
        )}
        <div className="flex gap-2 mb-4">
          <button
            onClick={downloadTemplate}
            className="flex items-center gap-1.5 text-xs text-[#A0A0A0] hover:text-white border border-white/10 rounded-lg px-3 py-2"
          >
            <Download size={13} /> Download template
          </button>
          <button
            onClick={() => fileRef.current?.click()}
            className="flex items-center gap-1.5 text-xs bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-white"
          >
            <Upload size={13} /> Choose CSV
          </button>
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFile} />
        </div>
        {rows.length > 0 && (
          <div className="mb-4 bg-[#0A0A0A] border border-white/10 rounded-lg p-3 max-h-48 overflow-y-auto">
            <div className="text-xs text-[#A0A0A0] mb-2">{rows.length} row{rows.length !== 1 ? "s" : ""} ready</div>
            {rows.slice(0, 5).map((r, i) => (
              <div key={i} className="text-xs py-1 border-b border-white/5 last:border-0">
                <span className="text-white">{r.name}</span> — <span className="text-[#A0A0A0]">{r.phone}</span>
                {r.dob && <span className="text-[#A0A0A0]"> · DOB {r.dob}</span>}
                {r.anniversary && <span className="text-[#A0A0A0]"> · Anniv {r.anniversary}</span>}
              </div>
            ))}
            {rows.length > 5 && <div className="text-xs text-[#A0A0A0] pt-1">…and {rows.length - 5} more</div>}
          </div>
        )}
        {errors.length > 0 && (
          <div className="mb-4 bg-red-500/10 border border-red-500/30 rounded-lg p-3 max-h-32 overflow-y-auto">
            <div className="text-xs font-bold text-red-400 mb-1">Errors ({errors.length})</div>
            {errors.map((e, i) => (
              <div key={i} className="text-xs text-red-300">{e.phone}: {e.error}</div>
            ))}
          </div>
        )}
        <button
          onClick={doImport}
          disabled={importing || !rows.length}
          className="w-full bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-50 text-white font-semibold rounded-lg py-3 text-sm"
        >
          {importing ? "Importing…" : `Import ${rows.length || ""} Customers`}
        </button>
      </div>
    </div>
  );
}
